import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LoginResponse, User } from '../types';
import { login as apiLogin, loginWithGoogle as apiGoogleLogin, verifyToken } from '../api/auth';

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (identifier: string, password: string) => Promise<boolean>;
  loginWithGoogle: (credential: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  clearError: () => void;
}

type AuthStateSetter = (state: Partial<AuthState>) => void;

async function authenticate(
  set: AuthStateSetter,
  request: () => Promise<LoginResponse>,
  fallbackMessage: string
): Promise<boolean> {
  set({ isLoading: true, error: null });

  try {
    const response = await request();
    if (response.code !== 0 || !response.data) {
      set({ error: response.message || fallbackMessage, isLoading: false });
      return false;
    }

    localStorage.setItem('token', response.data.token);
    set({
      token: response.data.token,
      user: response.data.userInfo,
      isAuthenticated: true,
      isLoading: false,
    });
    return true;
  } catch (error: any) {
    set({
      error: error.response?.data?.message || fallbackMessage,
      isLoading: false,
    });
    return false;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (identifier: string, password: string) => authenticate(
        set,
        () => apiLogin(identifier, password),
        'Sign-in failed. Check your connection and try again.'
      ),

      loginWithGoogle: (credential: string) => authenticate(
        set,
        () => apiGoogleLogin(credential),
        'Google sign-in failed. Please try again.'
      ),

      logout: () => {
        window.google?.accounts.id.disableAutoSelect();
        localStorage.removeItem('token');
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
        });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false });
          return false;
        }

        try {
          const response = await verifyToken();
          if (response.code === 0 && response.data) {
            set({
              token,
              user: response.data,
              isAuthenticated: true,
            });
            return true;
          } else {
            get().logout();
            return false;
          }
        } catch {
          get().logout();
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
